'use client'

import { Box, Button, Flex, Stack } from '@mantine/core'
import Image from 'next/image'
import Link from 'next/link'
import { useParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useMemo, useState } from 'react'

import OnChainTips from '@/app/[locale]/price/[slug]/_components/Trade/OnChainTips'
import BaseIcon from '@/components/basic/BaseIcon'
import CoinIcon from '@/components/CoinIcon'
import { SectionErrorBoundary } from '@/components/error/boundary'
import { DEFAULT_CURRENCY, fiatCurrencies, getCurrencyInfo } from '@/configs/currencies'
import { useSsrContext } from '@/contexts/ssr'
import { isAPACAndMEACountry, isCAUser, isUserInRestrictedRegion, isUSUser } from '@/i18n/country'
import { useI18nRouter } from '@/i18n/navigation'
import { setSelectedCurrency } from '@/server/actions'
import { AllAvailableCurrencyCodes } from '@/types/currency'
import type { Currency } from '@/types/global'
import { CurrencyType } from '@/types/global'

import { useConverter } from '../../_hooks/useConverter'
import CurrencySelectModal from '../Modal/CurrencySelectModal'
import TokenSelectModal from '../Modal/TokenSelectModal'
import { ConverterHeader } from './ConverterHeader'
import { ConverterInput } from './ConverterInput'
import styles from './index.module.css'
import NotTradableBanner from './NotTradableBanner'
import { RateDisplay } from './RateDisplay'

interface ConverterProps {
  currency: AllAvailableCurrencyCodes
  basePath?: string
  customHeader?: React.ReactNode
  selectTitle?: string
  searchPlaceholder?: string
  showNotTradableBanner?: boolean
  onChainHref?: string
}

function Converter({
  currency,
  basePath = '',
  customHeader,
  selectTitle,
  searchPlaceholder,
  showNotTradableBanner = false,
  onChainHref,
}: ConverterProps) {
  const router = useI18nRouter()
  const tConverter = useTranslations('converter')
  const [isTokenModalOpen, setIsTokenModalOpen] = useState(false)
  const [isCurrencyModalOpen, setIsCurrencyModalOpen] = useState(false)

  const {
    coin,
    tokenText,
    fiatText,
    oneTokenToFiatPrice,
    isRefreshing,
    isRatesFetching,
    handleTokenChange,
    handleFiatChange,
    handleRefresh,
  } = useConverter(currency)

  const { countryCode } = useSsrContext()
  const params = useParams<{ locale: string }>()
  const locale = params.locale
  const tradable = coin.app_tradable || coin.exchange_tradable
  const isRestricted = isUserInRestrictedRegion(coin.restricted_areas, countryCode, locale)
  const isAPACAndMEA = isAPACAndMEACountry(countryCode)
  const isCA = isCAUser(countryCode)
  const isUS = isUSUser(countryCode)
  const isSupportedOnchain = isAPACAndMEA || isCA || isUS

  const showOnchainTips = !tradable && !isRestricted && isSupportedOnchain

  const currentCurrency = useMemo(() => getCurrencyInfo(currency) || DEFAULT_CURRENCY, [currency])

  const currencyIcon =
    currentCurrency.type === CurrencyType.Fiat ? (
      <Box
        style={{
          border: '2px solid white',
          borderRadius: '50%',
          overflow: 'hidden',
          display: 'flex',
        }}
      >
        <Image src={currentCurrency.iconUrl} alt={currentCurrency.code} width={16} height={18} />
      </Box>
    ) : (
      <BaseIcon src={currentCurrency.iconUrl} size={20} circle alt={currentCurrency.code} />
    )

  return (
    <Box maw={{ base: '100%', lg: 410 }}>
      <Flex direction={{ base: 'column' }} gap={24} justify={'start'}>
        {customHeader ?? (
          <ConverterHeader
            coinSymbol={coin.symbol}
            coinName={coin.name}
            currency={currency}
            currencyName={currentCurrency.name}
          />
        )}

        <Box w={{ base: '100%', lg: 410 }} style={{ borderRadius: 24 }}>
          <Stack gap={24}>
            <ConverterInput
              value={tokenText}
              onChange={handleTokenChange}
              onSelectorClick={() => setIsTokenModalOpen(true)}
              icon={<CoinIcon coin={coin} />}
              label={coin.symbol}
            />

            <ConverterInput
              value={fiatText}
              onChange={handleFiatChange}
              onSelectorClick={() => setIsCurrencyModalOpen(true)}
              icon={currencyIcon}
              label={currentCurrency.code}
            />

            <RateDisplay
              coinSymbol={coin.symbol}
              coinName={coin.name}
              currency={currency}
              rate={oneTokenToFiatPrice}
              isRefreshing={isRefreshing || isRatesFetching}
              onRefresh={handleRefresh}
            />

            {/* Inline button for desktop */}
            {!isRestricted && !showOnchainTips && (
              <Box className={styles.inlineButton}>
                <Link href={`https://web.crypto.com/hub/price/${coin.slug}`}>
                  <Button
                    fullWidth
                    radius="md"
                    variant="filled-primary"
                    fw={600}
                    fz={16}
                    lh="20px"
                    disabled={!tradable}
                  >
                    {tConverter('buy_token', { token: coin.symbol })}
                  </Button>
                </Link>
              </Box>
            )}

            <Box className={styles.onchainTipsContainer}>
              {showOnchainTips && <OnChainTips symbol={coin.symbol} href={onChainHref} />}
              {showNotTradableBanner && !tradable && !isRestricted && !isSupportedOnchain && (
                <NotTradableBanner symbol={coin.symbol} />
              )}
            </Box>
          </Stack>
        </Box>
      </Flex>

      {/* Sticky button for tablet/mobile */}
      {!isRestricted && tradable && (
        <Box className={styles.stickyButtonContainer}>
          <Link href={`https://web.crypto.com/hub/price/${coin.slug}`} style={{ width: '100%' }}>
            <Button
              fullWidth
              radius="md"
              variant="filled-primary"
              fw={600}
              fz={16}
              lh="20px"
              h={44}
            >
              {tConverter('buy_token', { token: coin.symbol })}
            </Button>
          </Link>
        </Box>
      )}

      {showOnchainTips && (
        <Box className={styles.responsiveOnChainContainer}>
          <OnChainTips symbol={coin.symbol} isMobile href={onChainHref} />
        </Box>
      )}

      {showNotTradableBanner && !tradable && !isRestricted && !isSupportedOnchain && (
        <Box className={styles.responsiveNotTradableBannerContainer}>
          <NotTradableBanner symbol={coin.symbol} />
        </Box>
      )}

      <TokenSelectModal
        visible={isTokenModalOpen}
        onClose={() => setIsTokenModalOpen(false)}
        currentCurrency={currency}
        basePath={basePath}
        selectTitle={selectTitle}
        searchPlaceholder={searchPlaceholder}
      />

      <CurrencySelectModal
        visible={isCurrencyModalOpen}
        onClose={() => setIsCurrencyModalOpen(false)}
        currentCurrencyCode={currency}
        onSelect={async (item: Currency) => {
          setIsCurrencyModalOpen(false)
          if (fiatCurrencies.some(c => c.code === item.code)) {
            await setSelectedCurrency(item.code)
          }
          router.push(`${basePath}/${coin.slug}/${item.code.toLowerCase()}`)
        }}
      />
    </Box>
  )
}

export default SectionErrorBoundary.with(Converter)
